import type { LocationGenerics } from "@/router/locationGenerics";

import { connectionsRoute } from "@/pages/Connections/routes";
import { ApolloClient } from "@apollo/client";
import { Navigate, Route } from "@tanstack/react-location";

import { AccessGroupsGuard } from "./AccessGroups/AccessGroupsGuard";
import { SettingsLayout } from "./Layout";

export const settingsRoute = (
  client: ApolloClient<object>,
): Route<LocationGenerics> => {
  return {
    path: "settings",
    meta: {
      breadcrumb: () => "Settings",
    },
    element: <SettingsLayout />,
    children: [
      {
        path: "account",
        element: () =>
          import("./Account/AccountSettingsPage").then((m) => <m.default />),
        meta: {
          breadcrumb: () => "Delete Account",
        },
      },
      {
        path: "transform",
        element: () =>
          import("./Transform/TransformPage").then((m) => <m.TransformPage />),
        meta: {
          breadcrumb: () => "Transform",
        },
      },
      {
        path: "data-warehouse",
        meta: {
          breadcrumb: () => "Data Warehouse",
        },
        element: () =>
          import("./DataWarehouse/DataWarehousePage").then((m) => (
            <m.default />
          )),
      },
      {
        path: "elt-destinations",
        meta: {
          breadcrumb: () => "ELT Destinations",
        },
        element: () =>
          import("./EltDestinations/EltDestinationsPage").then((m) => (
            <m.default />
          )),
      },
      {
        path: "notifications",
        meta: {
          breadcrumb: () => "Notifications",
        },
        element: () => import("./Notifications").then((m) => <m.default />),
      },
      {
        path: "audit-logs",
        meta: {
          breadcrumb: () => "Audit Logs",
        },
        element: () =>
          import("./AuditLogs/AuditLogsPage").then((m) => <m.default />),
      },
      {
        path: "api-keys",
        meta: {
          breadcrumb: () => "API Keys",
        },
        element: () =>
          import("./ApiKeys/ApiKeysPage").then((m) => <m.default />),
      },
      {
        path: "access-groups",
        meta: {
          breadcrumb: () => "Access groups",
        },
        // Guard is the route element so neither page — nor its admin-only query —
        // mounts for a non-admin.
        element: <AccessGroupsGuard />,
        children: [
          {
            path: ":accessGroupId",
            meta: {
              breadcrumb: () => "Access group",
            },
            element: () =>
              import("./AccessGroups/AccessGroupDetailPage").then((m) => (
                <m.default />
              )),
          },
          {
            element: () =>
              import("./AccessGroups/AccessGroupsPage").then((m) => (
                <m.default />
              )),
          },
        ],
      },
      {
        path: "ai",
        meta: {
          breadcrumb: () => "AI",
        },
        element: () => import("./AI/AiSettingsPage").then((m) => <m.default />),
      },
      {
        path: "mcp-server",
        meta: {
          breadcrumb: () => "MCP Server",
        },
        element: () =>
          import("./McpServer/McpServerPage").then((m) => <m.default />),
      },
      {
        path: "users",
        element: <Navigate to="/settings/members" />,
      },
      {
        path: "members",
        meta: {
          breadcrumb: () => "Members",
        },
        element: () =>
          import("./Members/MembersPage").then((m) => <m.default />),
      },
      {
        path: "download",
        meta: {
          breadcrumb: () => "Import / Export SQL",
        },
        element: () =>
          import("./Download/DownloadPage").then((m) => <m.default />),
      },
      {
        path: "user-profile",
        meta: {
          breadcrumb: () => "User Settings",
        },
        element: () =>
          import("./user/UserSettingsPage").then((m) => <m.UserSettingsPage />),
      },
      {
        path: "plan",
        meta: {
          breadcrumb: () => "Plan & Billing",
        },
        children: [
          {
            path: "billing",
            element: () =>
              import("./plan/billing/Page").then((m) => <m.default />),
          },
          {
            element: () =>
              import("./plan/plan/Page").then((m) => <m.default />),
          },
        ],
      },
      {
        path: "usage",
        element: () => import("./plan/usage/Page").then((m) => <m.default />),
        meta: {
          breadcrumb: () => "Usage",
        },
      },
      {
        // Legacy redirect for old billing path
        path: "billing",
        element: <Navigate to="../plan" />,
      },
      {
        path: "github",
        children: [
          {
            path: "connect",
            element: () =>
              import("@/features/github-sync").then((m) => (
                <m.GitHubConnectPage />
              )),
          },
          {
            element: () => import("./github/Page").then((m) => <m.default />),
          },
        ],
      },
      connectionsRoute(client),
      {
        element: <Navigate to={`./connections`} />,
      },
    ],
  };
};
